import { Link } from "react-router-dom";
import { ArrowUpRight, Hammer, Ruler } from "lucide-react";

const HERO =
    "https://customer-assets.emergentagent.com/job_timber-workspace/artifacts/un7x647z_IMG_4570.png";

const KITCHEN =
    "https://customer-assets.emergentagent.com/job_timber-workspace/artifacts/xynarg0a_IMG_4557.jpg";
const OFFICE =
    "https://customer-assets.emergentagent.com/job_timber-workspace/artifacts/r9jnrw1s_IMG_4566.png";

export default function Home() {
    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative -mt-20 h-screen min-h-[680px] w-full overflow-hidden">
                <img
                    src={HERO}
                    alt="BAU-Paks Kft. — egyedi gardrób tolóajtós kivitelben, fa hatású polcrendszerrel, Paks"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#1C1714]/60 via-[#1C1714]/35 to-[#1C1714]/70" />
                <div className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-12 flex flex-col justify-end pb-20 md:pb-28">
                    <p
                        className="overline text-[#D4A373] reveal"
                        data-testid="hero-overline"
                    >
                        BAU-PAKS Kft. — 1996 óta, Paks
                    </p>
                    <h1
                        data-testid="hero-heading"
                        className="font-serif mt-6 text-5xl sm:text-6xl md:text-7xl lg:text-[96px] leading-[0.95] tracking-tight text-[#F9F6F0] max-w-5xl reveal reveal-delay-1"
                    >
                        Egyedi bútor és
                        <br />
                        <span className="italic font-normal text-[#D4A373]">
                            asztalosipar Pakson.
                        </span>
                    </h1>
                    <p
                        data-testid="hero-subtext"
                        className="mt-8 text-base md:text-lg text-[#F9F6F0]/85 max-w-xl leading-relaxed reveal reveal-delay-2"
                    >
                        Egyedi bútor készítése — konyhabútor,
                        irodabútor, beépített bútor és épületasztalos-ipari
                        munkák. Bútorgyártó Pakson 1996 óta, korszerű
                        gépparkkal és tapasztalt szakemberekkel.
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row gap-4 reveal reveal-delay-3">
                        <Link
                            to="/ajanlatkeres"
                            data-testid="hero-cta-primary"
                            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#C98A4B] text-[#1C1714] text-sm font-semibold tracking-wide hover:bg-[#D4A373] transition-colors duration-500"
                        >
                            Kérjen egyedi ajánlatot
                            <ArrowUpRight size={16} />
                        </Link>
                        <Link
                            to="/termekek"
                            data-testid="hero-cta-secondary"
                            className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-[#F9F6F0]/40 text-[#F9F6F0] text-sm font-medium tracking-wide hover:bg-[#F9F6F0]/10 transition-colors duration-500"
                        >
                            Munkáink megtekintése
                        </Link>
                    </div>
                </div>
            </section>

            {/* INTRO / PHILOSOPHY */}
            <section className="py-24 md:py-36">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
                    <div className="lg:col-span-5">
                        <p className="overline">Filozófiánk</p>
                        <h2 className="font-serif mt-5 text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#2C2724]">
                            Bútor, ami Önnek
                            <span className="italic text-[#C98A4B]">
                                {" "}
                                készül
                            </span>
                            .
                        </h2>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7 space-y-6 text-[#5C534D] leading-relaxed">
                        <p className="text-lg">
                            A BAU-PAKS Kft. 1996 januárja óta dolgozik
                            épületasztalos-ipari termékek gyártásán,
                            beépítésén, valamint kapcsolódó kivitelezési
                            és szakipari munkákon — stabil paksi
                            telephellyel és megbízható szakmai háttérrel.
                        </p>
                        <p className="text-sm">
                            Tevékenységünk jelentős része az MVM Paksi
                            Atomerőmű Zrt. területén végzett munkákhoz
                            kapcsolódik. Korszerű faipari gépparkunknak
                            köszönhetően további megrendelő partnereket
                            is hatékonyan tudunk kiszolgálni.
                        </p>
                    </div>
                </div>
            </section>

            {/* THREE COLUMN VALUES */}
            <section className="pb-24 md:pb-36">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-2 gap-12">
                    {[
                        {
                            icon: Hammer,
                            title: "Egyedi bútorgyártás",
                            body: "Egyedi bútor készítése Pakson — minden darab saját méretre, saját tervre. A látványtervtől a beépítésig egy csapat dolgozik a bútoron.",
                        },
                        {
                            icon: Ruler,
                            title: "Milliméter pontosság",
                            body: "3D-s terv, helyszíni méretvétel, tökéletes illeszkedés — korszerű gépparkunkkal.",
                        },
                    ].map((v) => (
                        <div
                            key={v.title}
                            data-testid={`value-${v.title
                                .toLowerCase()
                                .replace(/\s/g, "-")}`}
                            className="border-t border-[#4A3728]/20 pt-8"
                        >
                            <v.icon
                                size={28}
                                strokeWidth={1.5}
                                className="text-[#C98A4B]"
                            />
                            <h3 className="font-serif text-3xl mt-6 text-[#2C2724]">
                                {v.title}
                            </h3>
                            <p className="mt-3 text-sm text-[#5C534D] leading-relaxed">
                                {v.body}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* ABOUT (RÓLUNK) — text-only */}
            <section
                id="rolunk"
                className="relative py-24 md:py-32 bg-[#F3EBE1]"
            >
                <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                    <div className="lg:col-span-4">
                        <p className="overline">Rólunk</p>
                        <h2 className="font-serif mt-5 text-4xl md:text-5xl lg:text-[64px] leading-[1.05] tracking-tight text-[#2C2724]">
                            BAU-Paks Kft. —
                            <br />
                            <span className="italic text-[#C98A4B]">
                                1996 óta.
                            </span>
                        </h2>
                    </div>
                    <div className="lg:col-span-7 lg:col-start-6 space-y-5 text-[#3C342E] leading-relaxed">
                        <p>
                            A BAU-Paks Szolgáltató Korlátolt
                            Felelősségű Társaság 1996 januárjában kezdte
                            meg működését. Fő tevékenységeink:
                            épületasztalos-ipari termékek
                            gyártása-beépítése, festés, szigetelési
                            munkák, állványozási munkák és lakatos
                            munkák.
                        </p>
                        <p>
                            Társaságunk munkavállalóinak létszáma
                            jelenleg 12 fő. Festő- és mázoló, lakatos,
                            valamint asztalos munkakörben dolgozó
                            szakembereinkkel komplex építőipari munkák
                            elvégzésére tudunk vállalkozni.
                        </p>
                        <p>
                            Számos visszatérő ügyféllel dolgozunk
                            együtt, tevékenységünk jelentős része az
                            MVM Paksi Atomerőmű Zrt. területén végzett
                            munkákhoz kapcsolódik. Korszerű faipari
                            gépeinknek köszönhetően további megrendelő
                            partnereket is hatékonyan tudunk
                            kiszolgálni.
                        </p>
                        <p>
                            Minden munkánknál az egyediség, a minőség
                            és a tartósság a legfontosabb szempont.
                            Olyan megoldásokat alakítunk ki, amelyek
                            pontosan illeszkednek ügyfeleink műszaki és
                            esztétikai elvárásaihoz.
                        </p>
                    </div>
                </div>
            </section>

            {/* SERVICES PREVIEW */}
            <section className="py-24 md:py-36">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                        <div>
                            <p className="overline">Szolgáltatásaink</p>
                            <h2 className="font-serif mt-5 text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#2C2724]">
                                Amiben
                                <span className="italic text-[#C98A4B]">
                                    {" "}
                                    otthon
                                </span>{" "}
                                vagyunk.
                            </h2>
                        </div>
                        <Link
                            to="/szolgaltatasok"
                            data-testid="home-services-all-link"
                            className="inline-flex items-center gap-2 text-sm font-semibold tracking-wide text-[#4A3728] hover:text-[#C98A4B] transition-colors"
                        >
                            Összes szolgáltatás
                            <ArrowUpRight size={14} />
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                        {[
                            {
                                img: KITCHEN,
                                label: "01",
                                title: "Konyhabútor",
                                desc: "Egyedi konyhabútor tervezéstől beépítésig.",
                            },
                            {
                                img: OFFICE,
                                label: "02",
                                title: "Irodabútor",
                                desc: "Íróasztalok és konferenciaasztalok.",
                            },
                        ].map((s) => (
                            <Link
                                key={s.title}
                                to="/szolgaltatasok"
                                data-testid={`home-service-${s.title.toLowerCase()}`}
                                className="group relative h-[460px] md:h-[540px] overflow-hidden block"
                            >
                                <img
                                    src={s.img}
                                    alt={`${s.title} — BAU-Paks Kft. egyedi asztalosmunka, Paks`}
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#1C1714]/85 via-[#1C1714]/20 to-transparent" />
                                <div className="absolute bottom-0 left-0 right-0 p-8 md:p-10 text-[#F9F6F0]">
                                    <p className="font-serif text-sm text-[#D4A373] mb-3">
                                        / {s.label}
                                    </p>
                                    <h3 className="font-serif text-4xl md:text-5xl tracking-tight">
                                        {s.title}
                                    </h3>
                                    <p className="mt-2 text-sm text-[#F9F6F0]/80 max-w-sm">
                                        {s.desc}
                                    </p>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA STRIP */}
            <section className="bg-[#4A3728] text-[#F9F6F0]">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 md:py-32 text-center">
                    <p className="overline text-[#D4A373]">Kezdjük el</p>
                    <h2 className="font-serif mt-5 text-4xl md:text-6xl lg:text-7xl leading-tight tracking-tight max-w-4xl mx-auto">
                        Meséljen a projektjéről —
                        <span className="italic text-[#D4A373]">
                            {" "}
                            mi megépítjük.
                        </span>
                    </h2>
                    <Link
                        to="/ajanlatkeres"
                        data-testid="home-cta-bottom"
                        className="inline-flex items-center gap-2 mt-12 px-10 py-4 bg-[#C98A4B] text-[#1C1714] text-sm font-semibold tracking-wide hover:bg-[#D4A373] transition-colors duration-500"
                    >
                        Ingyenes ajánlatkérés
                        <ArrowUpRight size={16} />
                    </Link>
                </div>
            </section>
        </div>
    );
}
